import React, { useEffect, useState } from "react";
import { Button, Card, Image, Input, Popconfirm, Space, Table } from "antd";
import { type TableProps } from "antd/es/table";
import keyBy from "lodash.keyby";
import round from "lodash.round";
import { useTranslation } from "react-i18next";

import { nutritionActions, RequestStatus } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { type Dish } from "@fitness-app/data-models/entities/Dish";
import { generateUniqId } from "@fitness-app/utils/src/helpers/generateUniqId";
import { calculateMeasureMultiplier } from "@fitness-app/utils/src/nutrition/calculateMeasureMultiplier";
import { recalculateMeasures } from "@fitness-app/utils/src/nutrition/recalculateMeasures";
import { sumNutrients } from "@fitness-app/utils/src/nutrition/sumNutrients";

import ModalForm from "~/components/ModalForm/ModalForm";
import { usePermission } from "~/hooks/usePermission";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { useEntityChange } from "~/hooks/useEntityChange";
import { useFormHandler } from "~/hooks/useFormHandler";
import DishForm from "~/modules/Nutrition/DishForm/DishForm";
import { type DishFormModel } from "~/modules/Nutrition/DishForm/types";
import { useNutritionMeasures } from "~/modules/Nutrition/hooks/useNutritionMeasures";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { useUpdateDishInTemplate } from "../hooks/useUpdateDishInTemplate";

const NutritionDishes = (): React.ReactElement => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("nutrition");
  const [searchValue, setSearchValue] = useState("");
  const { dishes, dishesPage, dishesPerPage, dishesStatus, dishesTotalPages, dishSearchTerm } = useAppSelector(
    (state) => state.nutrition,
  );
  const { measuresMap } = useNutritionMeasures();

  const { userId } = useUserRole();
  // Usunięcie z katalogu jest nieodwracalne dla całego tenanta.
  const canDeleteFromCatalog = usePermission(PermissionKey.NUTRITION_MANAGE, PermissionScope.ALL);
  const [loading, onSuccess, onFailure, onStart] = useEntityChange();
  const [deleting, onSuccessDelete, onFailureDelete, onStartDeleting] = useEntityChange();
  const { showModalForm, toggleModalForm, openFormModal, formModel, editedEntity, hideFormModal, clearModel } =
    useFormHandler<DishFormModel, Dish>();
  const { updateDishInTemplate } = useUpdateDishInTemplate("nutrition_dishes");

  useEffect(() => {
    void dispatch(nutritionActions.fetchDishes());
  }, [dispatch]);

  const onSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      if (dishSearchTerm) {
        onClear();
      }
      return;
    }
    void dispatch(nutritionActions.searchDish({ searchTerm: trimmed }));
  };

  const onClear = () => {
    setSearchValue("");
    dispatch(nutritionActions.clearSearchingDish());
  };

  const handleChange: TableProps<Dish>["onChange"] = (pagination) => {
    if (pagination.current !== dishesPage) {
      void dispatch(nutritionActions.fetchDishes({ page: pagination.current }));
    }
  };

  const onSaveDish = async (
    formData: DishFormModel,
    { portionSize, calories }: { portionSize: number; calories: number },
  ) => {
    const { image, ingredients, updateTemplates, ...rest } = formData;

    if (formModel && editedEntity) {
      const byKey = keyBy(editedEntity.ingredients, "id");
      const dish: Dish = {
        ...editedEntity,
        ...rest,
        media: Array.isArray(image) && image.length ? [{ url: image[0] || "", type: "coverPhoto" }] : null,
        updatedAt: new Date().toISOString(),
        portionSize,
        calories,
        ingredients: formData.ingredients.map((ingredient) => ({
          ...(byKey[ingredient.id] || {}),
          ...ingredient,
          calories: ingredient.nutrients.calories,
          portionCalories: ingredient.nutrients.calories
            ? round((ingredient.nutrients.calories * ingredient.portion) / 100, 1)
            : 0,
        })),
      };

      try {
        onStart();
        await dispatch(nutritionActions.updateDish({ dish, dishId: editedEntity.id })).unwrap();
        if (updateTemplates) {
          await updateDishInTemplate(editedEntity.id, editedEntity);
        }
        onSuccess();
        hideFormModal();
      } catch (e) {
        onFailure();
      }
    } else {
      const dish: Dish = {
        ...rest,
        media: Array.isArray(image) && image.length ? [{ url: image[0] || "", type: "coverPhoto" }] : null,
        id: generateUniqId(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        type: "dish",
        createdBy: userId,
        metadata: {
          parentId: null,
        },
        source: "custom",
        portionSize,
        calories,
        verified: true,
        visible: true,
        ingredients: formData.ingredients.map((ingredient) => ({
          ...ingredient,
          calories: ingredient.nutrients.calories,
          portionCalories: ingredient.nutrients.calories
            ? round((ingredient.nutrients.calories * ingredient.portion) / 100, 1)
            : 0,
        })),
      };

      try {
        onStart();
        await dispatch(nutritionActions.addDish({ dish })).unwrap();
        onSuccess();
        toggleModalForm(false);
      } catch (e) {
        onFailure();
      }
    }
  };

  const deleteDish = async (id: string) => {
    onStartDeleting(id);
    try {
      await dispatch(nutritionActions.deleteDish(id)).unwrap();
      onSuccessDelete();
    } catch {
      onFailureDelete();
    }
  };

  const handleEdit = (dish: Dish) => {
    const model: DishFormModel = {
      id: dish.id,
      image: dish.media?.map((media) => media.url) || [],
      tags: dish.tags || [],
      name: dish.name,
      includes: dish.includes || [],
      dishTypes: dish.dishTypes || [],
      kitchenTypes: dish.kitchenTypes || [],
      cookTime: dish.cookTime || null,
      size: dish.size || 2,
      preparationSteps: dish.preparationSteps || [],
      portions: dish.portions || 1,
      needsCooking: dish.needsCooking ?? false,
      needsReheating: dish.needsReheating ?? false,
      mealTypes: dish.mealTypes || [],
      prepTime: dish.prepTime || null,
      ingredients: dish.ingredients.map((ingredient) => {
        const enhancedIngredient = recalculateMeasures(ingredient);
        const { multiplier } = calculateMeasureMultiplier(enhancedIngredient, measuresMap);

        return {
          ...enhancedIngredient,
          multiplier,
        };
      }),
    };
    openFormModal(model, dish);
  };

  return (
    <>
      <Card
        extra={
          <Button
            onClick={() => {
              clearModel();
              toggleModalForm(true);
            }}
            type="primary"
          >
            {t("dish.addNewDish")}
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }} size={24}>
          <div className="flex w-full items-center">
            <Input.Search
              placeholder={t<string>("dish.searchDishPlaceholder")}
              onSearch={onSearch}
              loading={dishesStatus === RequestStatus.SEARCHING}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ width: 300, margin: "20px 0" }}
            />
            {Boolean(searchValue) && (
              <Button onClick={onClear} type="link">
                {t("reset")}
              </Button>
            )}
          </div>
          <Table<Dish>
            dataSource={dishes}
            rowKey="id"
            loading={dishesStatus === RequestStatus.FETCHING}
            scroll={{ x: true }}
            pagination={{
              pageSize: dishesPerPage,
              total: dishesTotalPages * dishesPerPage,
              current: dishesPage,
              showSizeChanger: false,
            }}
            onChange={handleChange}
          >
            <Table.Column<Dish>
              title={t("dish.image")}
              dataIndex="image"
              key="image"
              render={(_, row) => (
                <Image
                  width={80}
                  src={row.media?.[0]?.url}
                  alt={row.name}
                  fallback="data:image/png;base64,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"
                />
              )}
            />

            <Table.Column<Dish> title={t("dish.name")} dataIndex="name" key="name" />
            <Table.Column<Dish>
              title={t("ingredientsTable.servingSize")}
              dataIndex="portionSize"
              key="portionSize"
              render={(packageSize: number) => packageSize || 100}
            />
            <Table.Column<Dish> title={t("ingredientsTable.calories")} dataIndex="calories" key="calories" />
            <Table.Column<Dish>
              title={t("ingredientsTable.protein")}
              dataIndex="protein"
              key="protein"
              render={(_, row) => sumNutrients(row.ingredients, "protein")}
            />
            <Table.Column<Dish>
              title={t("ingredientsTable.carbs")}
              dataIndex={["nutrients", "carbohydrates"]}
              key="carbs"
              render={(_, row) => sumNutrients(row.ingredients, "carbohydrates")}
            />
            <Table.Column<Dish>
              title={t("ingredientsTable.fat")}
              dataIndex={["nutrients", "fat"]}
              key="fat"
              render={(_, row) => sumNutrients(row.ingredients, "fat")}
            />
            <Table.Column<Dish>
              title={t("common:list.options")}
              key="action"
              align="left"
              render={(_, row) => (
                <Space>
                  <Button type="link" onClick={() => handleEdit(row)}>
                    {t("common:list.edit")}
                  </Button>
                  {canDeleteFromCatalog && (
                    <Popconfirm
                      overlayStyle={{ maxWidth: 400 }}
                      placement="leftTop"
                      title={t("dish.deleteWarning")}
                      onConfirm={() => deleteDish(row.id)}
                    >
                      <Button type="link" danger loading={deleting === row.id}>
                        {t("common:button.delete")}
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              )}
            />
          </Table>
        </Space>
      </Card>
      <ModalForm
        open={showModalForm}
        onCancel={() => toggleModalForm(false)}
        title={formModel ? t("dish.editDish") : t("dish.addNewDish")}
        width={900}
        loading={!!loading}
      >
        <DishForm onSubmit={onSaveDish} model={formModel} showUpdateTemplates />
      </ModalForm>
    </>
  );
};

export default NutritionDishes;
